import React, { useRef, useState, useEffect } from "react";
import { ShieldCheck, Camera, X, Loader2, ImageUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const SelfieVerify = ({ open, onClose, onVerified }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [camError, setCamError] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setReady(true);
        setCamError(false);
      } catch (e) {
        setCamError(true);
        toast.error("Caméra indisponible. Utilisez le bouton « Envoyer un selfie » ci-dessous.");
      }
    })();
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      setReady(false);
    };
  }, [open]);

  const submitSelfie = async (file) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/verify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      toast.success("Profil vérifié ! Badge ajouté ✅");
      onVerified();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la vérification.");
    } finally {
      setSubmitting(false);
    }
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 640;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) { toast.error("Capture impossible."); return; }
    await submitSelfie(new File([blob], "selfie.jpg", { type: "image/jpeg" }));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image trop lourde (max 8 Mo)."); return; }
    await submitSelfie(f);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-kora-ink/80 backdrop-blur-sm flex items-end md:items-center justify-center" data-testid="verify-modal">
      <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-6 animate-float-up">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display font-bold text-2xl text-kora-ink flex items-center gap-2">
            <ShieldCheck size={22} className="text-kora-sage" /> Vérification
          </h2>
          <button onClick={onClose} className="text-kora-muted"><X size={22} /></button>
        </div>
        <p className="text-kora-muted text-sm mb-4">Prenez un selfie pour confirmer que c'est bien vous. Cela rassure les autres membres.</p>

        {!ready && !camError && (
          <div data-testid="camera-permission-hint" className="mb-3 bg-blue-50 border border-blue-300 rounded-2xl px-4 py-3 text-sm text-blue-800 font-medium">
            👆 Votre navigateur va demander l'accès à la caméra : choisissez <strong>« Autoriser »</strong> pour prendre votre selfie.
          </div>
        )}
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-[3/4] mb-4">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="selfie-video" />
          {!ready && <div className="absolute inset-0 flex items-center justify-center text-white/70"><Loader2 className="animate-spin" /></div>}
        </div>

        <button
          data-testid="capture-selfie-btn"
          onClick={capture}
          disabled={submitting || !ready}
          className="w-full bg-kora-sage text-white rounded-full py-4 font-bold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
          {submitting ? "Vérification…" : "Prendre le selfie"}
        </button>

        {camError && (
          <>
            <div className="flex items-center gap-3 my-3">
              <span className="flex-1 h-px bg-kora-line" />
              <span className="text-xs text-kora-muted font-semibold">OU</span>
              <span className="flex-1 h-px bg-kora-line" />
            </div>

            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="selfie-file-input" />
            <button
              data-testid="upload-selfie-btn"
              onClick={() => fileRef.current?.click()}
              disabled={submitting}
              className="w-full bg-white border-2 border-kora-sage text-kora-sage rounded-full py-3.5 font-bold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <ImageUp size={18} /> Envoyer un selfie (photo ou galerie)
            </button>
            <p className="text-[11px] text-kora-muted text-center mt-2">Caméra bloquée ? Utilisez ce bouton : votre selfie passera par la même vérification.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default SelfieVerify;
